<script setup>
// One sleeve of the bar, side-on, built from photographs of real plates.
//
// WHY PHOTOS AND STILL RECOLOURABLE. Plate colour is seed data on the plate
// record — a lifter with black 25s recolours them in Kit and every drawing in
// the app follows. A photograph of a red plate cannot become green, so each
// plate ships as GREYSCALE with its alpha, and the record's colour is blended
// back in: `background-blend-mode: luminosity` takes hue and saturation from
// the colour and lightness from the photograph, and the same file masks the
// element so the bevelled edge stays a bevelled edge. Both things stay true —
// it looks like the plate, and the kit still owns the colour.
//
// The sizes below are MEASURED OFF THE PHOTOGRAPHS, not from a spec table.
// These are competition bumpers, and a bumper is far thicker relative to its
// diameter than a calibrated steel disc: six 25s is 372mm on a 415mm sleeve,
// which is why a heavy bar really does run out of room.
//
// THE BAR RUNS IN FROM THE LEFT EDGE OF THE SCREEN. The figure bleeds out of
// its parent's left padding by `--bleed` (the page sets it to its own padding;
// default 16px), so the knurled handle starts at x=0, sharp — the photograph
// is cropped there and nothing fades it. That is the design: the bar is bigger
// than the phone, and the phone shows you the end of it that matters.
//
// ONE SCALE FOR EVERYTHING. The whole photograph (handle, cuff, sleeve) spans
// `--sleeve-span` — 88% of the viewport by default, clamped for tablets; a page
// may set its own — and every plate, the collar and every gap are drawn at that
// same mm→px. See the CSS at the bottom for how the box is sized.
//
// AN EMPTY BAR HAS NO COLLAR ON IT. The collar is drawn against the last plate,
// so with no plates there is nothing to draw it against: a bare sleeve.
//
// LOADING, ANIMATED (`animate`, off unless a page asks). When the stack changes
// the drawing does what a lifter does: the collar comes off and is set aside,
// plates coming off slide along the sleeve and away to the right, plates going
// on come in from past the right edge of the screen — fast through the air,
// slow along the sleeve, a clunk and a haptic as each one seats — and the
// collar goes back on. `lib/motion.js` is the clock; this file only draws it.
// Transforms and opacity only, and the still drawing is ALWAYS the end state:
// every animation is laid over it, so cancelling one anywhere leaves exactly
// the drawing there would have been without it.
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { describe, fmt } from '../lib/loading.js'
import { choreograph, TIMING } from '../lib/motion.js'
import { prefersReducedMotion } from '../core/a11y.js'
import { haptic } from '../core/util.js'

const props = defineProps({
  stack: { type: Array, required: true },   // per side, heaviest inside
  added: { type: Array, default: () => [] },  // the outer tail that is new this set
  removed: { type: Array, default: () => [] }, // coming off, drawn dashed past the sleeve tip
  plates: { type: Array, required: true },   // kit records: { weight, colour }
  unit: { type: String, default: 'kg' },
  collarWeight: { type: Number, default: 0 },
  // A smaller default scale (Kit's sample bar, the scheme's rows). The page can
  // still set `--sleeve-span` to anything.
  compact: Boolean,
  // The small round plate faces under the bar. Off by default now: the design
  // prints the per-side list as words instead. Kept, and still removable.
  faces: { type: Boolean, default: false },
  // Count mode only: every plate IN THE DRAWING becomes a button that takes
  // THAT plate off. The ramp never wants this — there the loading is the
  // instruction, not a guess.
  removable: Boolean,
  // The per-side sentence under the drawing (plus "Sleeve's full" when it is).
  // Off by default: pages print their own per-side line in their own type.
  // The rig carries the same sentence as its aria-label either way.
  caption: { type: Boolean, default: false },
  // Animate plates going on and off when `stack` changes. OFF by default, so a
  // drawing that is a picture of a kit or a list (Kit's sample, the warm-ups)
  // never moves; the Ramp and Bar pass the lifter's setting. Reduce Motion
  // switches it off here too, whatever a page passes.
  animate: { type: Boolean, default: false },
})
// `remove(index)` — a plate tapped off, by its position in `stack`.
// `full(boolean)` — whether the sleeve has run out of room; fires once on
// mount and again whenever it changes, so a page can say "Sleeve's full" in
// its own words and place.
const emit = defineEmits(['remove', 'full'])

// [diameter mm, thickness mm], measured from the photographs at 0.5263 mm/px.
// The change plates are the design's figures; they have no photograph of
// their own and wear the white plate's (see `artFor`).
const DISCS = {
  25: [450, 62], 20: [443, 54], 15: [435, 42],
  10: [368, 43], 5: [259, 38], 2.5: [244, 37],
  1.25: [200, 20], 0.5: [160, 15], 0.25: [140, 12],
}
const ART = ['25', '20', '15', '10', '5', '2.5']

const kgOf = (w) => (props.unit === 'lb' ? +(w * 0.4536).toFixed(2) : w)

// WHICH PHOTOGRAPH. A pale plate — the white 5, the silver change plates, any
// plate the lifter has coloured white or silver — is drawn on the WHITE plate's
// photo, because luminosity blending keeps the photo's lightness: a silver
// record on the black 2.5's photo came out gunmetal. Decided by the record's
// colour, never by weight, so a gym with black change plates gets them black.
// Anything else wears the nearest photographed size.
function artFor(w, pale) {
  if (pale) return '5'
  const kg = kgOf(w)
  const key = String(kg)
  if (ART.includes(key)) return key
  let best = '2.5'
  let gap = Infinity
  for (const k of ART) { const d = Math.abs(Number(k) - kg); if (d < gap) { gap = d; best = k } }
  return best
}
function dims(w) {
  const kg = kgOf(w)
  if (DISCS[kg]) return DISCS[kg]
  const r = Math.min(1, kg / 25)
  return [Math.round(150 + 300 * Math.sqrt(r)), Math.round(20 + 42 * r)]
}

// THE BAR PHOTOGRAPH, measured. public/kit/bar-sleeve.webp is 1745x272 and runs
// from the knurled handle, through the cuff, to the sleeve tip; the sleeve
// proper is 1165px of it and starts at the cuff face, 574px in. At a 415mm
// sleeve that is 0.356 mm per source pixel, so the whole image is 621.6mm and
// handle plus cuff is 204.5mm.
//
// The handle has to be VISIBLE — the knurl is what says "this is the bar you
// grip" — and the cuff has to be visible too: it is the shoulder the innermost
// plate sits against, and it is what makes the plates read as held BETWEEN cuff
// and collar. (sleeve.webp, the older crop, stays in public/kit for anything
// still pointing at it.)
const MM_PER_SRC_PX = 415 / 1165
const SLEEVE_MM = 415                    // usable sleeve: cuff face to tip
const BAR_MM = 1745 * MM_PER_SRC_PX      // 621.6, the whole photograph
const LEAD_MM = 574 * MM_PER_SRC_PX      // 204.5, handle + cuff
// Past the sleeve tip, where a plate coming off is drawn.
const GHOST_GAP = 26
const GHOST_STEP = 14
// Measured off public/kit/collar.webp (578x440): the barrel is 37% of the
// photograph's width and sets the SCALE only.
const COLLAR_BARREL_W = 0.37
// Where the collar TOUCHES THE PLATE: the face ring on the left of the body,
// 12.1% in. Positioning by the barrel (24% in) pushed that ring 20mm into the
// last plate, so the collar looked jammed into the stack.
const COLLAR_FACE_X = 0.121
// The body's vertical centre, 60.6% down — the lever sits above it, so centring
// the whole photograph on the axis drew the collar low on the sleeve.
const COLLAR_BODY_Y = 0.606

function tone(hex) {
  const m = (hex || '').replace('#', '').match(/../g)
  if (!m || m.length < 3) return { pale: true, dark: false }
  const rgb = m.slice(0, 3).map((h) => parseInt(h, 16) / 255)
  const lin = rgb.map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))
  const lum = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2]
  const chroma = Math.max(...rgb) - Math.min(...rgb)
  // Pale = light AND unsaturated: white and silver, not yellow (yellow is
  // bright but it is a colour, and on the white photo it washes out).
  return { pale: lum > 0.3 && chroma < 0.2, dark: lum < 0.05 }
}
const recordFor = (w) => props.plates.find((p) => Number(p.weight) === Number(w)) || {}

const drawing = computed(() => {
  // The collar photograph is scaled by its BARREL (61mm on a competition
  // collar, 20mm for a spring clip) and positioned by its face ring. The lever
  // arm hangs past the barrel, which is what a collar actually does on a bar;
  // only the barrel counts toward the fit.
  const collarMM = props.collarWeight >= 1 ? [124, 61] : [96, 20]
  const need = props.stack.reduce((a, w) => a + dims(w)[1], 0) + (props.stack.length ? collarMM[1] : 0)
  const squeeze = Math.min(1, SLEEVE_MM / (need || 1))

  let x = LEAD_MM
  const firstNew = props.stack.length - props.added.length
  const discs = props.stack.map((w, i) => {
    const [h, t] = dims(w)
    const colour = recordFor(w).colour || null
    const { pale, dark } = tone(colour)
    const d = {
      key: `${i}-${w}`, w, x, t: t * squeeze, h,
      art: `/kit/edge-${artFor(w, pale)}.webp`,
      colour, pale, dark,
      isNew: i >= firstNew,
    }
    x += t * squeeze
    return d
  })
  // Where the collar goes, and whether it is on at all: not on an empty bar.
  // `face` is where its face ring touches the last plate.
  const barrelW = collarMM[1] * squeeze
  const imgW = barrelW / COLLAR_BARREL_W
  const collar = { imgW, imgX: x - imgW * COLLAR_FACE_X, face: x, on: discs.length > 0 }

  let gx = BAR_MM + GHOST_GAP
  const ghosts = props.removed.map((w, i) => {
    const [h, t] = dims(w)
    const g = { key: `g${i}`, w, x: gx, t, h }
    gx += t + GHOST_STEP
    return g
  })

  // THE BOX IS AS TALL AS THE TALLEST PLATE THIS KIT OWNS, not the tallest one
  // on the bar right now. Tapping a 25 onto an empty bar in Count mode must not
  // move a single pixel of the screen below the drawing, and a ramp going from
  // a 10 to a 25 between sets must not either. The kit does not change while
  // the screen is up, so neither does the box.
  const height = Math.max(
    collarMM[0],
    ...props.plates.map((p) => dims(Number(p.weight) || 0)[0]),
    ...discs.map((d) => d.h),
    ...ghosts.map((g) => g.h),
  )
  // The collar's lever counts: on a full sleeve it hangs past the tip.
  const width = Math.max(
    BAR_MM,
    collar.on ? collar.imgX + collar.imgW : 0,
    props.removed.length ? gx - GHOST_STEP : 0,
  ) + 2
  return { discs, collar, ghosts, width, height, full: squeeze < 1 }
})

// Percentages of the rig, so the whole drawing scales with one number.
const pcX = (mm) => `${(mm / drawing.value.width) * 100}%`
const pcH = (mm) => `${(mm / drawing.value.height) * 100}%`
// Just under a plate of this diameter, for its "new" or "off".
const under = (mm) => `calc(50% + ${(mm / drawing.value.height) * 50}% + 4px)`

// Where the "new" and "off" words go: the middle of each group (the new plates
// are always the outer tail; the removed ones sit together past the tip), on a
// line under the tallest plate drawn.
const tags = computed(() => {
  const { discs, ghosts } = drawing.value
  const fresh = discs.filter((d) => d.isNew)
  const mid = (xs) => (xs.length ? (xs[0].x + xs[xs.length - 1].x + xs[xs.length - 1].t) / 2 : 0)
  const h = Math.max(0, ...discs.map((d) => d.h), ...ghosts.map((g) => g.h))
  return { fresh: fresh.length ? mid(fresh) : 0, off: ghosts.length ? mid(ghosts) : 0, h }
})

const label = computed(() => describe({ stack: props.stack, added: props.added, removed: props.removed }))
const full = computed(() => drawing.value.full)
watch(full, (f) => emit('full', f), { immediate: true })
defineExpose({ full })

// NEAREST PLATE WINS. Each plate's button is at least 44px wide, centred on the
// plate — and plates are 7–34px thick at phone scale, so neighbouring buttons
// overlap. The browser gives a tap to whichever button is on top, which is
// simply the later one in the stack; so a pointer tap is re-aimed at the plate
// whose centre is nearest the finger. A keyboard or screen-reader activation
// has no position (`detail` is 0) and means exactly the button it was on.
function hit(event, i) {
  let pick = i
  if (event.detail > 0 && Number.isFinite(event.clientX)) {
    const buttons = event.currentTarget.parentElement?.children ?? []
    let best = Infinity
    Array.from(buttons).forEach((b, n) => {
      const r = b.getBoundingClientRect()
      const d = Math.abs(r.left + r.width / 2 - event.clientX)
      if (d < best) { best = d; pick = n }
    })
  }
  emit('remove', pick)
}

// ---- loading, animated --------------------------------------------------------
// The easing of each stretch of a plate's trip, tuned by eye on frame captures.
// A plate going on is still moving when it reaches the plate before it — that
// is the clunk — and rides CLUNK_PX into it before it settles back.
const EASE = {
  fly: 'cubic-bezier(0.25, 0.35, 0.6, 0.84)',  // in through the air, slowing toward the tip
  slide: 'cubic-bezier(0.3, 0.45, 0.6, 0.9)',  // along the sleeve, slower, not yet stopped
  settle: 'cubic-bezier(0.3, 0, 0.4, 1)',      // back off the plate it landed on
  unstick: 'cubic-bezier(0.45, 0, 0.8, 0.5)',  // coming off: slowly along the sleeve…
  away: 'cubic-bezier(0.3, 0.3, 0.75, 0.8)',   // …then carried off, quicker
  collarOff: 'cubic-bezier(0.5, 0, 0.8, 0.5)',
  collarOn: 'cubic-bezier(0.15, 0.7, 0.3, 1)',
}
const CLUNK_PX = 1.5
// The collar, set aside: slid off until its face ring is this far past the tip,
// fading as it goes, and brought back from there.
const AWAY_MM = 40
const px = (v) => `${v.toFixed(2)}px 0px`

const boxEl = ref(null)
const artEl = ref(null)
// Plates on their way off. Not in `stack` any more, so drawn only while they go.
const leaving = ref([])
// The collar on its way off a bar that is now empty, and so has no collar.
const collarOut = ref(false)
const showCollar = computed(() => drawing.value.collar.on || collarOut.value)

// Heavier plate, heavier clunk; the collar is always the lighter click.
const thud = (w) => (kgOf(w) >= 15 ? 'heavy' : kgOf(w) >= 5 ? 'pop' : 'tick')

let run = null
let runs = 0

// CUT SHORT, NEVER QUEUED. A change that arrives mid-animation — a quick tap in
// Count, Clear the bar — ends the running one where it was going and starts
// the next at once. Cancelling is enough to end it: the still drawing under the
// animations IS their end state. Nothing waits and nothing lags the taps.
function stop() {
  if (!run) return
  for (const a of run.anims) a.cancel()
  for (const t of run.timers) clearTimeout(t)
  run = null
  leaving.value = []
  collarOut.value = false
}

const canAnimate = () => props.animate && !prefersReducedMotion()
  && typeof document !== 'undefined' && !document.hidden
  && typeof document.documentElement.animate === 'function'

// Before the re-render (the default flush), so the plates coming off can be
// drawn in the same frame the stack loses them; the animations start after it,
// still before the browser paints, so the new still drawing never flashes up.
watch(drawing, (now, before) => {
  const was = before.discs.map((d) => d.w)
  const is = now.discs.map((d) => d.w)
  if (was.length === is.length && was.every((w, i) => w === is[i])) return // a colour, not a load
  // A change cut short part-way has the collar off the bar still: leave it off.
  const collarAway = !!run
  stop()
  if (!canAnimate()) return
  const plan = choreograph(was, is, { collarAway })
  if (!plan) return
  const id = ++runs
  run = { id, anims: [], timers: [] }
  leaving.value = plan.offs.map((o) => ({ ...before.discs[o.i], key: `off${id}-${o.i}`, i: o.i }))
  collarOut.value = !now.collar.on && !!plan.collar.off
  nextTick(() => play(id, plan, before, now))
})
watch(() => props.animate, (on) => { if (!on) stop() })
onBeforeUnmount(stop)

function play(id, plan, before, now) {
  if (run?.id !== id) return
  const art = artEl.value
  const r = art?.getBoundingClientRect()
  if (!r?.width) { stop(); return }
  // ONE MEASUREMENT, then only transforms. The scale is the drawing's own; the
  // edge is the screen's, because the box is clipped there (see the CSS).
  const scale = r.width / now.width
  const edge = boxEl.value.getBoundingClientRect().right
  const beyond = (x) => edge - (r.left + x * scale) + 2 // from x mm to wholly past the edge
  const toTip = (x) => Math.max(0, (BAR_MM - x) * scale)
  const { anims, timers } = run

  // Off: slowly along the sleeve, then carried off past the edge of the screen.
  for (const o of plan.offs) {
    const el = art.querySelector(`[data-off="${o.i}"]`)
    if (!el) continue
    const d = before.discs[o.i]
    const far = beyond(d.x)
    anims.push(el.animate([
      { translate: px(0), easing: EASE.unstick },
      { offset: TIMING.offSplit, translate: px(Math.min(toTip(d.x), far * 0.35)), easing: EASE.away },
      { translate: px(far) },
    ], { duration: o.dur, delay: o.start, fill: 'both' }))
  }

  // On: most of the distance through the air in the first stretch, the rest
  // slowly along the sleeve, into the plate before it, and settle. The haptic
  // is the clunk, at the moment it lands.
  for (const o of plan.ons) {
    const el = art.querySelector(`[data-i="${o.i}"]`)
    if (!el) continue
    const d = now.discs[o.i]
    const far = beyond(d.x)
    anims.push(el.animate([
      { translate: px(far), easing: EASE.fly },
      { offset: TIMING.onSplit, translate: px(Math.min(toTip(d.x), far * 0.35)), easing: EASE.slide },
      { offset: TIMING.seat, translate: px(-CLUNK_PX), easing: EASE.settle },
      { translate: px(0) },
    ], { duration: o.dur, delay: o.start, fill: 'backwards' }))
    timers.push(setTimeout(() => haptic(thud(d.w)), o.seatAt))
  }

  // The collar: from where it was, off past the tip and fading; back from
  // there to its new seat. Laid out in ms, then turned into offsets. On a bar
  // left empty it goes and stays gone; onto a bar that was empty it only comes.
  const collar = art.querySelector('.collar')
  const { off, on } = plan.collar
  if (collar && (off || on)) {
    const from = (before.collar.imgX - now.collar.imgX) * scale
    const away = (BAR_MM + AWAY_MM - now.collar.face) * scale
    const end = on ? on.end : plan.end
    const move = off
      ? [{ t: 0, translate: px(from), easing: EASE.collarOff }, { t: off.end, translate: px(away) }]
      : [{ t: 0, translate: px(away) }]
    const fade = off
      ? [{ t: 0, opacity: 1 }, { t: off.end * 0.35, opacity: 1 }, { t: off.end, opacity: 0 }]
      : [{ t: 0, opacity: 0 }]
    if (on) {
      move.push({ t: on.start, translate: px(away), easing: EASE.collarOn }, { t: on.end, translate: px(0) })
      fade.push({ t: on.start, opacity: 0 }, { t: on.start + (on.end - on.start) * 0.35, opacity: 1 }, { t: on.end, opacity: 1 })
      timers.push(setTimeout(() => haptic('tick'), on.end - 15))
    } else {
      move.push({ t: end, translate: px(away) })
      fade.push({ t: end, opacity: 0 })
    }
    const frames = (pts) => pts.map(({ t, ...k }) => ({ ...k, offset: Math.min(1, t / end) }))
    const opts = { duration: end, fill: on ? 'none' : 'forwards' }
    anims.push(collar.animate(frames(move), opts), collar.animate(frames(fade), opts))
  }

  // "new", "off" and the dashed outlines say what changed; they fade in as the
  // collar goes back on, once the plates they describe have got there.
  for (const el of art.querySelectorAll('.ghost, .tag')) {
    anims.push(el.animate(
      [{ opacity: 0 }, { offset: plan.reveal / plan.end, opacity: 0 }, { opacity: 1 }],
      { duration: plan.end },
    ))
  }

  Promise.all(anims.map((a) => a.finished)).then(() => {
    if (run?.id !== id) return
    run = null
    leaving.value = []
    collarOut.value = false
  }, () => { /* cancelled: stop() has already tidied up */ })
}
</script>

<template>
  <figure
    class="sleeve"
    :class="{ compact, animated: animate }"
    :style="{
      '--wr': drawing.width / BAR_MM,
      '--hr': drawing.height / BAR_MM,
      '--ar': `${drawing.width} / ${drawing.height}`,
    }"
  >
    <div ref="boxEl" class="barbox">
      <div class="rig">
        <div ref="artEl" class="art" role="img" :aria-label="label">
          <img class="bar" src="/kit/bar-sleeve.webp" alt="" aria-hidden="true" :style="{ width: pcX(BAR_MM) }">

          <span
            v-for="(d, i) in drawing.discs"
            :key="d.key"
            class="disc"
            :data-i="i"
            :class="{ pale: d.pale, rim: d.dark }"
            :style="{ left: pcX(d.x), width: pcX(d.t), height: pcH(d.h) }"
          ><span
            class="tint"
            :style="{ '--art': `url(${d.art})`, '--tint': d.colour || 'var(--faint, #A0A3A6)' }"
          /></span>

          <!-- Plates on their way off, where they were, only while they go. -->
          <span
            v-for="d in leaving"
            :key="d.key"
            class="disc"
            :data-off="d.i"
            :class="{ pale: d.pale, rim: d.dark }"
            :style="{ left: pcX(d.x), width: pcX(d.t), height: pcH(d.h) }"
          ><span
            class="tint"
            :style="{ '--art': `url(${d.art})`, '--tint': d.colour || 'var(--faint, #A0A3A6)' }"
          /></span>

          <img
            v-if="showCollar"
            class="collar"
            src="/kit/collar.webp"
            alt=""
            aria-hidden="true"
            :style="{ left: pcX(drawing.collar.imgX), width: pcX(drawing.collar.imgW), transform: `translateY(-${COLLAR_BODY_Y * 100}%)` }"
          >

          <span
            v-for="g in drawing.ghosts"
            :key="g.key"
            class="ghost"
            :style="{ left: pcX(g.x), width: pcX(g.t), height: pcH(g.h) }"
          />

          <!-- What changed this set, in words, ONCE per group and on ONE line
               under the tallest plate: a label under each plate sat at that
               plate's own height, so two new plates of different sizes put
               their words at two heights, tucked under a neighbour. -->
          <span v-if="tags.fresh" class="tag" :style="{ left: pcX(tags.fresh), top: under(tags.h) }">new</span>
          <span v-if="tags.off" class="tag" :style="{ left: pcX(tags.off), top: under(tags.h) }">off</span>
        </div>

        <!-- OUTSIDE the role="img": a button inside an image is presentational
             and a screen reader would never reach it. -->
        <div v-if="removable && drawing.discs.length" class="hits">
          <button
            v-for="(d, i) in drawing.discs"
            :key="`h${d.key}`"
            type="button"
            class="hit"
            :style="{ left: pcX(d.x + d.t / 2), width: `max(44px, ${pcX(d.t)})` }"
            :aria-label="`Take the ${fmt(d.w)} ${unit} off`"
            @click="hit($event, i)"
          />
        </div>
      </div>
    </div>

    <!-- THE FACES, when asked for. Small on purpose: the bar is the
         instruction, this is the confirmation — what you are reaching for,
         seen the way it looks in the rack. One per plate per side. -->
    <ul v-if="faces" class="faces">
      <li v-for="(d, i) in drawing.discs" :key="`f${d.key}`" :class="{ fresh: d.isNew }">
        <component
          :is="removable ? 'button' : 'span'"
          :type="removable ? 'button' : undefined"
          class="facewrap"
          :class="{ tappable: removable }"
          :aria-label="removable ? `Take the ${fmt(d.w)} ${unit} off` : undefined"
          @click="removable && emit('remove', i)"
        >
          <span
            class="face"
            :style="{ '--art': `url(/kit/face-${artFor(d.w, false)}.webp)`, '--tint': d.colour || 'var(--faint, #A0A3A6)' }"
            :class="{ pale: d.pale }"
          />
          <span v-if="removable" class="x" aria-hidden="true">×</span>
          <span class="fw">{{ fmt(d.w) }}</span>
        </component>
      </li>
    </ul>

    <figcaption v-if="!compact && caption">
      {{ label }}<span v-if="full"> Sleeve's full, check this fits your bar.</span>
    </figcaption>
  </figure>
</template>

<style scoped>
/* SIZING, in one place.
 *
 *   --sleeve-span   how wide the bar PHOTOGRAPH is drawn (handle to tip).
 *                   Default min(88vw, 400px); compact min(54vw, 250px).
 *                   The mm→px scale is span / 621.6mm, for everything.
 *   --bleed         the parent's left padding; the figure pulls out by it so
 *                   the handle starts at the screen edge. Default 16px. The
 *                   drawing may also use the same amount on the right.
 *
 * THE BOX IS FIXED. Its height is the kit's tallest plate at that scale, plus
 * room under it for a "new" or "off" — computed from the kit, not the stack,
 * so adding or removing a plate never moves the rest of the screen.
 *
 * TO SCALE IN BOTH DIRECTIONS. The rig takes its width from the scale and its
 * height from aspect-ratio, so a plate is never stretched to fill a box (the
 * old `width: 100%; max-height` rig drew every plate 2.3x too thick). If the
 * drawing would run past the right edge — several plates coming off, or a
 * full sleeve's collar lever — the rig takes the smaller of that and the room
 * there is, and the whole drawing shrinks together inside the same box. */
.sleeve {
  --_span: var(--sleeve-span, min(88vw, 400px));
  --_bleed: var(--bleed, 16px);
  --_label-room: 18px;
  margin: 0 0 0 calc(-1 * var(--_bleed));
  padding: 0;
}
.sleeve.compact { --_span: var(--sleeve-span, min(54vw, 250px)); }

.barbox {
  position: relative;
  height: calc(var(--_span) * var(--hr) + var(--_label-room));
}

.rig {
  position: relative;
  width: min(calc(100% + var(--_bleed)), calc(var(--_span) * var(--wr)));
  aspect-ratio: var(--ar);
}

.art, .hits { position: absolute; inset: 0; }

/* The bar sits behind everything, pinned to the axis the plates centre on. */
.bar {
  position: absolute; left: 0; top: 50%; transform: translateY(-50%);
  height: auto; max-width: none;
  pointer-events: none; user-select: none;
}

.disc { position: absolute; top: 50%; transform: translateY(-50%); }
.tint { display: block; width: 100%; height: 100%; }

/* THE TINT. Lightness from the photograph, hue and saturation from the kit
   record; the same file masks the element so the bevel survives. */
.tint, .face {
  background-color: var(--tint);
  background-image: var(--art);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-blend-mode: luminosity;
  -webkit-mask-image: var(--art);
  mask-image: var(--art);
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
}

/* EDGES, on the WRAPPER: a mask clips a drop-shadow drawn on the masked
   element itself, so the edge has to live one element out. A white or silver
   plate on the light ground gets a hairline; a black plate on the dark ground
   gets a faint rim. Each variable is transparent in the other scheme. */
.disc.pale {
  filter: drop-shadow(0 0 0.5px var(--pale-edge, #8A9197)) drop-shadow(0 0 0.5px var(--pale-edge, #8A9197));
}
.disc.rim {
  filter: drop-shadow(0 0 0.5px var(--dark-rim, transparent)) drop-shadow(0 0 0.5px var(--dark-rim, transparent));
}

.collar {
  position: absolute; top: 50%;
  height: auto; max-width: none;
  pointer-events: none; user-select: none;
}

/* Coming off: where it was, dashed, past the tip — not a plate, the gap one
   leaves. */
.ghost {
  position: absolute; top: 50%; transform: translateY(-50%);
  box-sizing: border-box;
  border: 1.5px dashed var(--faint, #A0A3A6);
  border-radius: 4px;
}

.tag {
  position: absolute;
  width: 40px;
  transform: translateX(-50%);
  text-align: center;
  font: 400 0.625rem/1.2 var(--face-mono, ui-monospace, monospace);
  letter-spacing: 0.06em;
  color: var(--dim, #6B6E71);
  white-space: nowrap;
  pointer-events: none;
}

/* MOTION lives in the script (Web Animations, `translate` and `opacity` only,
   laid over the still drawing) and only when a page passes `animate`.
 *
 * THE BOX REACHES THE SCREEN'S RIGHT EDGE AND CLIPS THERE, while animated. A
 * plate going on starts past the edge of the PHONE, not of the drawing, so the
 * box takes the page's right padding (the same --bleed as its left) and cuts
 * the plate off exactly at the glass — and a plate in flight can never make the
 * page scroll sideways. The rig's room is unchanged: it was already allowed to
 * run to that edge. Everything drawn fits inside the box's height, so clipping
 * the other axis too costs nothing (`hidden` for any web view without `clip`). */
.sleeve.animated .barbox {
  margin-right: calc(-1 * var(--_bleed));
  overflow: hidden;
  overflow: clip;
}
.sleeve.animated .rig { width: min(100%, calc(var(--_span) * var(--wr))); }

/* Invisible until touched or focused. Full height of the drawing: a finger
   aims at a plate, not at a point on it. */
.hit {
  position: absolute; top: 0; bottom: 0;
  transform: translateX(-50%);
  margin: 0; padding: 0; border: 0; border-radius: 8px;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.hit:active { background: color-mix(in srgb, var(--ink, #1A1B1C) 8%, transparent); }
.hit:focus-visible { outline: 2px solid var(--ink, #1A1B1C); outline-offset: -2px; }

/* ---- the faces row (opt-in) ---- */
.faces {
  display: flex; flex-wrap: wrap; align-items: flex-end; gap: 8px;
  margin: 8px 0 0 var(--_bleed); padding: 0; list-style: none;
  /* HOLDS ITS HEIGHT WHEN EMPTY, at the tappable height, so the first plate
     does not shove the screen down. */
  min-height: 52px;
}
.faces li { display: flex; }
.facewrap {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  background: none; border: 0; padding: 0; color: inherit;
}
.facewrap.tappable {
  position: relative; min-height: 52px; min-width: 44px;
  justify-content: center; padding: 2px 4px; border-radius: 8px; cursor: pointer;
}
.facewrap.tappable:active { background: var(--seg, #EFEFEC); }
.facewrap.tappable .face { width: 32px; height: 32px; }
.facewrap.tappable:focus-visible { outline: 2px solid var(--ink, #1A1B1C); outline-offset: 3px; }
/* The x is ink on the ground, never red: colour in this app means a weight. */
.x {
  position: absolute; top: -2px; right: -2px;
  width: 16px; height: 16px; border-radius: 50%;
  background: var(--ink, #1A1B1C); color: var(--bg, #FBFBF9);
  font: 600 12px/16px var(--face-mono, monospace); text-align: center;
}
.face { display: block; width: 24px; height: 24px; border-radius: 50%; }
.faces li.fresh .face { box-shadow: 0 0 0 2px var(--ink, #1A1B1C); }
.face.pale { box-shadow: inset 0 0 0 1px var(--pale-edge, #8A9197); }
.fw {
  font: 500 0.75rem/1 var(--face-mono, monospace);
  color: var(--dim, #6B6E71); font-variant-numeric: tabular-nums;
}

figcaption {
  margin: 6px 0 0 var(--_bleed);
  font: 400 0.875rem/1.4 var(--face-mono, monospace);
  color: var(--dim, #6B6E71);
}
</style>
